// SPDX-License-Identifier: LicenseRef-Proprietary

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  TreeNode, 
  TreeEdge, 
  ValidationIssue, 
  TreeState, 
  validateTree,
  validateEasyHTAParameters,
  DEFAULT_VALIDATION_RULES,
  type ModelMetadata,
  type CostParameters,
  type PopulationParameters,
  type SimulationParameters,
  DEFAULT_SIMULATION_PARAMETERS
} from '../lib/validation';
import { createVaccineComparisonTree } from '../lib/vaccineTreeTemplate';

// API format with nodes as array
export interface TreeStateForAPI {
  nodes: TreeNode[];
  edges: TreeEdge[];
  rootId: string | null;
}

export type NodeType = 'decision' | 'chance' | 'terminal';

export interface ViewPrefs {
  showProb: boolean;
  showCost: boolean;
  showQaly: boolean;
  showTransition: boolean;
  nodeSize: 's' | 'm' | 'l';
  fullscreen: boolean;
}

// EasyHTA parameter interfaces are imported from validation.ts

export interface AppState {
  // Tree data
  nodes: Record<string, TreeNode>;
  edges: TreeEdge[];
  rootId: string | null;
  selectedId: string | null;
  
  // View preferences
  view: ViewPrefs;
  
  // Validation
  issues: ValidationIssue[];
  
  // UI state
  showValidationDetails: boolean;
  showHelp: boolean;
  isImporting: boolean;
  
  // EasyHTA parameters
  modelMetadata: ModelMetadata;
  costParameters: CostParameters;
  populationParameters: PopulationParameters;
  simulationParameters: SimulationParameters;
  
  // Actions
  setNodes: (nodes: Record<string, TreeNode>) => void;
  setEdges: (edges: TreeEdge[]) => void;
  setRootId: (rootId: string | null) => void;
  setSelectedId: (selectedId: string | null) => void;
  setView: (view: Partial<ViewPrefs>) => void;
  setShowValidationDetails: (show: boolean) => void;
  setShowHelp: (show: boolean) => void;
  
  // EasyHTA parameter actions
  setModelMetadata: (metadata: Partial<ModelMetadata>) => void;
  setCostParameters: (params: Partial<CostParameters>) => void;
  setPopulationParameters: (params: Partial<PopulationParameters>) => void;
  setSimulationParameters: (params: Partial<SimulationParameters>) => void;
  
  // Tree operations
  addNode: (node: TreeNode) => void;
  updateNode: (id: string, updates: Partial<TreeNode>) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: TreeEdge) => void;
  updateEdge: (parentId: string, childId: string, updates: Partial<TreeEdge>) => void;
  removeEdge: (parentId: string, childId: string) => void;
  
  // Validation
  validate: () => void;
  
  // Utility
  getTreeState: () => TreeState;
  getTreeStateForAPI: () => TreeStateForAPI;
  clearTree: () => void;
  reset: () => void;
}

const STORAGE_KEY = 'easyhta:decision-tree:v1';

// Default EasyHTA parameters
const DEFAULT_MODEL_METADATA: ModelMetadata = {
  model_name: 'Health economic decision tree',
  currency: 'EUR',
  perspective: 'healthcare'
};

const DEFAULT_COST_PARAMETERS: CostParameters = {
  cost_vaccine_dose: 15,
  cost_admin: 5,
  cost_infection: 200,
  cost_productivity_per_case: 100,
  cost_adverse_event: 200,
  efficacy : 0.7,
  adverse_event_rate : 0.01,
  adverse_event_qaly_loss : 0,
  doses_per_course : 1,
  discount_vaccine_cost: 0.00
};

const DEFAULT_POPULATION_PARAMETERS: PopulationParameters = {
  population_size: 5_637_000,
  coverage_rate: 0.70,
  age_group: 'Adults',
  baseline_incidence: 0.05,
  baseline_mortality: 0.001,
  background_mortality: 0.005,
  utility_healthy: 1.0,
  utility_infection: 0.90,
  duration_infection: 7 / 365 // 7 days, matching dashboard default
};

// Debounced save function
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedSave = (state: AppState) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    try {
      const { nodes, edges, rootId, view, modelMetadata, costParameters, populationParameters, simulationParameters } = state;
      const dataToSave = { nodes, edges, rootId, view, modelMetadata, costParameters, populationParameters, simulationParameters };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save decision tree state:', error);
    }
  }, 500);
};

// Load from localStorage
const loadFromStorage = (): Partial<AppState> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      return {
        nodes: data.nodes || {},
        edges: data.edges || [],
        rootId: data.rootId || null,
        view: {
          showProb: false,
          showCost: false,
          showQaly: false,
          showTransition: false,
          nodeSize: 'm',
          fullscreen: false,
          ...data.view
        },
        modelMetadata: {
          ...DEFAULT_MODEL_METADATA,
          ...data.modelMetadata
        },
        costParameters: {
          ...DEFAULT_COST_PARAMETERS,
          ...data.costParameters
        },
        populationParameters: {
          ...DEFAULT_POPULATION_PARAMETERS,
          ...data.populationParameters
        },
        simulationParameters: {
          ...DEFAULT_SIMULATION_PARAMETERS,
          ...data.simulationParameters
        }
      };
    }
  } catch (error) {
    console.warn('Failed to load decision tree state:', error);
  }
  return {
    nodes: {},
    edges: [],
    rootId: null,
    view: {
      showProb: false,
      showCost: false,
      showQaly: false,
      showTransition: false,
      nodeSize: 'm',
      fullscreen: false
    },
    modelMetadata: DEFAULT_MODEL_METADATA,
    costParameters: DEFAULT_COST_PARAMETERS,
    populationParameters: DEFAULT_POPULATION_PARAMETERS,
    simulationParameters: DEFAULT_SIMULATION_PARAMETERS
  };
};

export const useVaccineClientStore = create<AppState>()(
  subscribeWithSelector((set, get) => {
    const loadedState = loadFromStorage();
    const initialState = {
      nodes: loadedState.nodes || {},
      edges: loadedState.edges || [],
      rootId: loadedState.rootId || null,
      view: loadedState.view || {
        showProb: false,
        showCost: false,
        showQaly: false,
        showTransition: false,
        nodeSize: 'm',
        fullscreen: false
      },
      selectedId: null,
      issues: [],
      showValidationDetails: false,
      showHelp: false,
      isImporting: false,
      modelMetadata: loadedState.modelMetadata || DEFAULT_MODEL_METADATA,
      costParameters: loadedState.costParameters || DEFAULT_COST_PARAMETERS,
      populationParameters: loadedState.populationParameters || DEFAULT_POPULATION_PARAMETERS,
      simulationParameters: loadedState.simulationParameters || DEFAULT_SIMULATION_PARAMETERS,
    };

    return {
      ...initialState,
      
      // Basic setters
    setNodes: (nodes: Record<string, TreeNode>) => {
      set({ nodes });
      debouncedSave(get());
      get().validate();
    },
    
    setEdges: (edges: TreeEdge[]) => {
      set({ edges });
      debouncedSave(get());
      get().validate();
    },
    
    setRootId: (rootId: string | null) => {
      set({ rootId });
      debouncedSave(get());
      get().validate();
    },
    
    setSelectedId: (selectedId: string | null) => set({ selectedId }),
    
    setView: (viewUpdates: Partial<ViewPrefs>) => {
      set((state) => ({ 
        view: { ...state.view, ...viewUpdates } 
      }));
      debouncedSave(get());
    },
    
    setShowValidationDetails: (showValidationDetails: boolean) => set({ showValidationDetails }),
    setShowHelp: (showHelp: boolean) => set({ showHelp }),
    
    // EasyHTA parameter actions
    setModelMetadata: (metadata: Partial<ModelMetadata>) => {
      set((state) => ({
        modelMetadata: { ...state.modelMetadata, ...metadata }
      }));
      debouncedSave(get());
      get().validate();
    },
    
    setCostParameters: (params: Partial<CostParameters>) => {
      set((state) => ({
        costParameters: { ...state.costParameters, ...params }
      }));
      debouncedSave(get());
      get().validate();
    },
    
    setPopulationParameters: (params: Partial<PopulationParameters>) => {
      set((state) => ({
        populationParameters: { ...state.populationParameters, ...params }
      }));
      debouncedSave(get());
      get().validate();
    },

    setSimulationParameters: (params: Partial<SimulationParameters>) => {
      set((state) => ({
        simulationParameters: { ...state.simulationParameters, ...params }
      }));
      debouncedSave(get());
      get().validate();
    },
    
    // Tree operations
    addNode: (node: TreeNode) => {
      set((state) => ({
        nodes: { ...state.nodes, [node.id]: node }
      }));
      debouncedSave(get());
      get().validate();
    },
    
    updateNode: (id: string, updates: Partial<TreeNode>) => {
      set((state) => {
        const node = state.nodes[id];
        if (!node) return state;
        
        return {
          nodes: {
            ...state.nodes,
            [id]: { ...node, ...updates }
          }
        };
      });
      debouncedSave(get());
      get().validate();
    },
    
    removeNode: (id: string) => {
      set((state) => {
        const newNodes = { ...state.nodes };
        const newEdges = state.edges.filter(
          edge => edge.parentId !== id && edge.childId !== id
        );
        
        // Remove node and all its descendants
        const removeNodeAndChildren = (nodeId: string) => {
          const node = newNodes[nodeId];
          if (node) {
            delete newNodes[nodeId];
            node.children.forEach(childId => removeNodeAndChildren(childId));
          }
        };
        
        removeNodeAndChildren(id);
        
        // Update root if it was removed
        const newRootId = state.rootId === id ? null : state.rootId;
        
        return {
          nodes: newNodes,
          edges: newEdges,
          rootId: newRootId,
          selectedId: state.selectedId === id ? null : state.selectedId
        };
      });
      debouncedSave(get());
      get().validate();
    },
    
    addEdge: (edge: TreeEdge) => {
      set((state) => ({
        edges: [...state.edges, edge]
      }));
      debouncedSave(get());
      get().validate();
    },
    
    updateEdge: (parentId: string, childId: string, updates: Partial<TreeEdge>) => {
      set((state) => ({
        edges: state.edges.map(edge =>
          edge.parentId === parentId && edge.childId === childId
            ? { ...edge, ...updates }
            : edge
        )
      }));
      debouncedSave(get());
      get().validate();
    },
    
    removeEdge: (parentId: string, childId: string) => {
      set((state) => ({
        edges: state.edges.filter(
          edge => !(edge.parentId === parentId && edge.childId === childId)
        )
      }));
      debouncedSave(get());
      get().validate();
    },
    
    // Validation
    validate: () => {
      const state = get();
      const treeState = state.getTreeState();
      const treeIssues = validateTree(treeState, DEFAULT_VALIDATION_RULES);
      const parameterIssues = validateEasyHTAParameters(
        state.modelMetadata,
        state.costParameters,
        state.populationParameters,
        state.simulationParameters
      );
      const issues = [...treeIssues, ...parameterIssues];
      set({ issues });
    },
    
    // Utility
    getTreeState: () => {
      const state = get();
      return {
        nodes: state.nodes,
        edges: state.edges,
        rootId: state.rootId
      };
    },
    
    getTreeStateForAPI: () => {
      const state = get();
      return {
        nodes: Object.values(state.nodes), // Convert object to array for backend
        edges: state.edges,
        rootId: state.rootId
      };
    },
    
    clearTree: () => {
      set({
        nodes: {},
        edges: [],
        rootId: null,
        selectedId: null,
        issues: []
      });
      debouncedSave(get());
    },

    reset: () => {
      const defaultTree = createVaccineComparisonTree({
        vaccineCost: DEFAULT_COST_PARAMETERS.cost_vaccine_dose,
        treatmentCost: DEFAULT_COST_PARAMETERS.cost_infection,
        efficacy: DEFAULT_COST_PARAMETERS.efficacy,
        baselineRisk: DEFAULT_POPULATION_PARAMETERS.baseline_incidence
      });

      set({
        nodes: defaultTree.nodes,
        edges: defaultTree.edges,
        rootId: defaultTree.rootId,
        selectedId: null,
        issues: [],
        showValidationDetails: false,
        showHelp: false,
        modelMetadata: DEFAULT_MODEL_METADATA,
        costParameters: DEFAULT_COST_PARAMETERS,
        populationParameters: DEFAULT_POPULATION_PARAMETERS,
        simulationParameters: DEFAULT_SIMULATION_PARAMETERS
      });
      debouncedSave(get());
      get().validate();
    }
    };
  })
);

// Subscribe to changes for auto-save
useVaccineClientStore.subscribe(
  (state) => ({ 
    nodes: state.nodes, 
    edges: state.edges, 
    rootId: state.rootId, 
    view: state.view,
    modelMetadata: state.modelMetadata,
    costParameters: state.costParameters,
    populationParameters: state.populationParameters,
    simulationParameters: state.simulationParameters
  }),
  (current, previous) => {
    if (current !== previous) {
      debouncedSave(useVaccineClientStore.getState());
    }
  }
);
